import {
  PARAMETRI_PREDEFINITI,
  TARATURA_NEUTRA,
  type Canale,
  type Impianto,
  type Zona,
} from '@emacomfort/domain';

/**
 * Impianto di riferimento Ecobel (interfaccia unificata): nessuna centralina
 * propria, quattro sistemi di terzi letti da connettori. La centralina di
 * terze parti espone undici canali di solo impianto — più di quanti ne alloca
 * la mappa Ema Comfort — ed è letta in sola lettura; i termostati non sono
 * comandabili dal cloud del produttore.
 */

const limiti = { min: 15, max: 26, impostatiDa: 'Ecobel — installazione' };

function sonda(id: string, moduloId: string, assegnazione: Canale['assegnazione'], extra: Partial<Canale> = {}): Canale {
  return {
    id,
    moduloId,
    direzione: 'INPUT',
    grandezza: 'TEMPERATURA',
    unita: '°C',
    tipoSonda: 'PT1000',
    taratura: { ...TARATURA_NEUTRA },
    assegnazione,
    confidenza: 'alta',
    ...extra,
  };
}

function zona(id: string, nome: string, icona: Zona['icona'], ordinamento: number, setpoint: number): Zona {
  return {
    id,
    nome,
    icona,
    ordinamento,
    canaleSonda: `T-${id}`,
    canaliAttuatore: [`V-${id}`],
    tipoCarico: 'ELETTROVALVOLA',
    abilitata: true,
    setpoint,
    sovrascritta: false,
    limiti: { ...limiti },
  };
}

export function impiantoEcobel(sovrascritture: Partial<Impianto> = {}): Impianto {
  const zone: Zona[] = [
    zona('soggiorno', 'Soggiorno', 'soggiorno', 1, 21),
    zona('cucina', 'Cucina', 'cucina', 2, 20.5),
    zona('camera', 'Camera', 'camera', 3, 19),
    zona('bagno', 'Bagno', 'bagno', 4, 22),
    zona('studio', 'Studio', 'studio', 5, 20),
  ];

  const canali: Canale[] = [
    ...zone.map((z) => sonda(`T-${z.id}`, 'termostati', { tipo: 'zona', zonaId: z.id }, { tipoSonda: 'DIGITALE' })),
    ...zone.map<Canale>((z) => ({
      id: `V-${z.id}`,
      moduloId: 'ta',
      direzione: 'OUTPUT',
      grandezza: 'COMANDO',
      unita: '',
      taratura: { ...TARATURA_NEUTRA },
      assegnazione: { tipo: 'zona', zonaId: z.id },
      confidenza: 'alta',
    })),
    sonda('S1', 'ta', { tipo: 'funzione', funzione: 'MANDATA_GENERATORE', generatoreId: 'g-geo' }),
    sonda('S2', 'ta', { tipo: 'funzione', funzione: 'RITORNO_GENERATORE', generatoreId: 'g-geo' }),
    sonda('S3', 'ta', { tipo: 'funzione', funzione: 'PUFFER_ALTO' }),
    sonda('S4', 'ta', { tipo: 'funzione', funzione: 'PUFFER_BASSO' }),
    sonda('S5', 'ta', { tipo: 'funzione', funzione: 'MANDATA_COLLETTORE' }),
    sonda('S6', 'ta', { tipo: 'funzione', funzione: 'RITORNO_COLLETTORE' }),
    sonda('S7', 'ta', { tipo: 'funzione', funzione: 'ACCUMULO_SANITARIO' }),
    sonda('S8', 'ta', { tipo: 'funzione', funzione: 'MANDATA_GENERATORE', generatoreId: 'g-camino' }),
    sonda('S9', 'ta', { tipo: 'funzione', funzione: 'RITORNO_GENERATORE', generatoreId: 'g-camino' }),
    sonda('S10', 'ta', { tipo: 'funzione', funzione: 'TEMPERATURA_ESTERNA' }),
    sonda('S11', 'ta', { tipo: 'funzione', funzione: 'UMIDITA_INTERNA' }, { grandezza: 'UMIDITA', unita: '%', tipoSonda: 'ANALOGICO' }),
    {
      id: 'P1',
      moduloId: 'ta',
      direzione: 'OUTPUT',
      grandezza: 'MODULAZIONE',
      unita: '%',
      taratura: { ...TARATURA_NEUTRA },
      assegnazione: { tipo: 'funzione', funzione: 'CIRCOLATORE' },
      confidenza: 'alta',
    },
    {
      id: 'HP',
      moduloId: 'pdc',
      direzione: 'OUTPUT',
      grandezza: 'COMANDO',
      unita: '',
      taratura: { ...TARATURA_NEUTRA },
      assegnazione: { tipo: 'funzione', funzione: 'COMANDO_GENERATORE', generatoreId: 'g-geo' },
      confidenza: 'media',
      nota: 'ecobel.HP',
    },
    {
      id: 'DHW',
      moduloId: 'ta',
      direzione: 'OUTPUT',
      grandezza: 'COMANDO',
      unita: '',
      taratura: { ...TARATURA_NEUTRA },
      assegnazione: { tipo: 'funzione', funzione: 'VALVOLA_DEVIATRICE' },
      confidenza: 'alta',
    },
  ];

  return {
    id: 'ecobel-villa-pino',
    tenantId: 'ecobel',
    nome: 'Villa Pino Torinese',
    indirizzo: { via: 'Strada della Collina 41', citta: 'Pino Torinese', provincia: 'TO' },
    cliente: 'Famiglia Bosco',
    dataInstallazione: '2024-05-20',
    tecnicoReferente: 'Tecnico Ecobel',
    centralina: { modello: 'Technische Alternative UVR16x2 (connettore)', firmware: 'connettore 0.3', tipo: 'terze_parti' },
    moduli: [
      { id: 'ta', tipo: 'CONNETTORE', nome: 'Centralina Technische Alternative', stato: 'ok', sorgenteId: 'ta' },
      { id: 'fv', tipo: 'CONNETTORE', nome: 'Inverter Solarman', stato: 'ok', sorgenteId: 'fv' },
      { id: 'pdc', tipo: 'CONNETTORE', nome: 'Pompa di calore', stato: 'ok', sorgenteId: 'pdc' },
      { id: 'termostati', tipo: 'CONNETTORE', nome: 'Termostati di marca', stato: 'ok', sorgenteId: 'termostati' },
    ],
    canali,
    zone,
    generatori: [
      { id: 'g-geo', nome: 'Pompa di calore geotermica', tipo: 'POMPA_DI_CALORE', canaleMandata: 'S1', canaleRitorno: 'S2', canaleComando: 'HP', priorita: 1, modulante: true },
      { id: 'g-camino', nome: 'Termocamino', tipo: 'TERMOCAMINO', canaleMandata: 'S8', canaleRitorno: 'S9', priorita: 2, modulante: false },
    ],
    sorgenti: [
      { id: 'ta', nome: 'Technische Alternative', tipo: 'centralina', comandabile: true, descrizione: 'sorgenti.ta' },
      { id: 'fv', nome: 'Solarman', tipo: 'fotovoltaico', comandabile: false, descrizione: 'sorgenti.fv' },
      { id: 'pdc', nome: 'Pompa di calore', tipo: 'pompa_di_calore', comandabile: false, descrizione: 'sorgenti.pdc' },
      { id: 'termostati', nome: 'Termostati', tipo: 'termostati', comandabile: false, descrizione: 'sorgenti.termostati' },
    ],
    sanitario: { presente: true, setpoint: 50, boostMinuti: 30 },
    vmc: { presente: false, modalita: 'SPENTA', sogliaUmidita: 60 },
    energia: { presente: true, sorgenteId: 'fv', potenzaPiccoKw: 6.5, capacitaBatteriaKwh: 10 },
    parametri: structuredClone(PARAMETRI_PREDEFINITI),
    modalita: 'RISCALDAMENTO',
    ...sovrascritture,
  };
}

export function impiantoEcobelBifamiliare(): Impianto {
  const base = impiantoEcobel();
  return {
    ...base,
    id: 'ecobel-chieri',
    nome: 'Bifamiliare Chieri',
    indirizzo: { via: 'Via Roma 7', citta: 'Chieri', provincia: 'TO' },
    cliente: 'Famiglia Serra',
    dataInstallazione: '2025-02-10',
    zone: base.zone.slice(0, 4),
    canali: base.canali.filter((c) => !c.id.endsWith('studio')),
    generatori: [base.generatori[0]!],
    energia: { presente: true, sorgenteId: 'fv', potenzaPiccoKw: 4.2, capacitaBatteriaKwh: 5 },
  };
}

export function impiantoEcobelCondominio(): Impianto {
  const base = impiantoEcobel();
  return {
    ...base,
    id: 'ecobel-moncalieri',
    nome: 'Condominio Moncalieri',
    indirizzo: { via: 'Corso Trieste 120', citta: 'Moncalieri', provincia: 'TO' },
    cliente: 'Amministrazione Corso Trieste',
    dataInstallazione: '2023-11-30',
    sorgenti: base.sorgenti.filter((s) => s.id !== 'termostati'),
    moduli: base.moduli.filter((m) => m.id !== 'termostati'),
    energia: { presente: true, sorgenteId: 'fv', potenzaPiccoKw: 12, capacitaBatteriaKwh: 0 },
  };
}
